import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Check if token is about to expire (within 5 minutes)
const isTokenExpiringSoon = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000; // Convert to milliseconds
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() > (expiresAt - fiveMinutes);
  } catch (e) {
    return true;
  }
};

// Silently refresh token before it expires
const refreshToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    // Call the refresh endpoint
    const response = await axios.post(`${API_BASE}/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const newToken = response.data.access_token;
    localStorage.setItem('token', newToken);
    return newToken;
  } catch (error) {
    // If refresh fails, clear storage and redirect
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    throw error;
  }
};

// Add token to requests and handle token refresh
// Request interceptor - add auth token
api.interceptors.request.use(async (config) => {
  // Skip auth header for public endpoints (login, register, portal auth)
  const publicEndpoints = ['/auth/login', '/auth/register', '/auth/request-password-reset', '/auth/reset-password', '/auth/validate-reset-token', '/portal/login', '/portal/request-reset', '/portal/reset-password', '/public/'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));
  
  if (isPublicEndpoint) {
    // Don't add any auth header for public endpoints
    return config;
  }
  
  // Use the standard token from localStorage
  let token = localStorage.getItem('token');
  
  // Check if token is expiring soon and proactively refresh
  if (token && isTokenExpiringSoon(token) && !config._retry) {
    try {
      token = await refreshToken();
    } catch (e) {
      // Token refresh failed, continue with existing token
      // The response interceptor will handle 401
    }
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with retry mechanism
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a portal user (different auth flow)
      const user = localStorage.getItem('user');
      const isPortalUser = user && JSON.parse(user).is_portal;
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const newToken = await refreshToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed - redirect to appropriate login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (isPortalUser) {
          window.location.href = '/portal/login';
        } else {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Universal Search API
export const searchAPI = {
  search: (query, limit = 10) => api.get('/search', { params: { q: query, limit } }),
};

// Reports & Analytics API
export const reportsAPI = {
  getProductLineAnalytics: () => api.get('/reports/product-line-analytics'),
  getSalesOverview: () => api.get('/reports/sales-overview'),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } }),
  getTeam: () => api.get('/users/team'),
  create: (userData) => api.post('/admin/users/create', userData),
  togglePortalAccess: (userId, hasAccess) => api.put(`/admin/users/${userId}/toggle-portal-access`, null, { params: { has_access: hasAccess } }),
  delete: (userId) => api.delete(`/users/${userId}`),
  updateStatus: (userId, isActive) => api.put(`/users/${userId}/status`, null, { params: { is_active: isActive } }),
  changePassword: (currentPassword, newPassword) => api.put('/users/me/change-password', { current_password: currentPassword, new_password: newPassword }),
  adminResetPassword: (userId, newPassword) => api.post('/admin/users/reset-password', { user_id: userId, new_password: newPassword }),
};

// Contacts API
export const contactsAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  bulkStatusUpdate: (contactIds, newStatus) => 
    api.post('/contacts/bulk-status-update', { contact_ids: contactIds, new_status: newStatus }),
  bulkAssign: (data) => api.post('/contacts/bulk-assign', data),
  autoDetectCustomers: () => api.post('/contacts/auto-detect-customers'),
  // Profile endpoints
  getProfile: (id) => api.get(`/contacts/${id}/profile`),
  getTimeline: (id, limit = 50) => api.get(`/contacts/${id}/timeline`, { params: { limit } }),
  getNotes: (id) => api.get(`/contacts/${id}/notes`),
  createNote: (id, data) => api.post(`/contacts/${id}/notes`, data),
  getEmails: (id) => api.get(`/contacts/${id}/emails`),
  createEmail: (id, data) => api.post(`/contacts/${id}/emails`, data),
  getCalls: (id) => api.get(`/contacts/${id}/calls`),
  createCall: (id, data) => api.post(`/contacts/${id}/calls`, data),
  getMeetings: (id) => api.get(`/contacts/${id}/meetings`),
  createMeeting: (id, data) => api.post(`/contacts/${id}/meetings`, data),
  getSubscriptions: (id) => api.get(`/contacts/${id}/subscriptions`),
  createSubscription: (id, data) => api.post(`/contacts/${id}/subscriptions`, data),
  getPayments: (id) => api.get(`/contacts/${id}/payments`),
  createPayment: (id, data) => api.post(`/contacts/${id}/payments`, data),
  getInvoices: (id) => api.get(`/contacts/${id}/invoices`),
  createInvoice: (id, data) => api.post(`/contacts/${id}/invoices`, data),
  getAttachments: (id) => api.get(`/contacts/${id}/attachments`),
  createAttachment: (id, data) => api.post(`/contacts/${id}/attachments`, data),
  uploadAttachment: (id, formData) => api.post(`/contacts/${id}/upload-attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPlaybooks: (id) => api.get(`/contacts/${id}/playbooks`),
  createPlaybook: (id, data) => api.post(`/contacts/${id}/playbooks`, data),
  getWorkflows: (id) => api.get(`/contacts/${id}/workflows`),
  createWorkflow: (id, data) => api.post(`/contacts/${id}/workflows`, data),
  getTickets: (id) => api.get(`/contacts/${id}/tickets`),
  createTicket: (id, data) => api.post(`/contacts/${id}/tickets`, data),
  getWarrantyClaims: (id) => api.get(`/contacts/${id}/warranty-claims`),
  getPortalFiles: (id) => api.get(`/contacts/${id}/portal-files`),
  getPortalChats: (id) => api.get(`/contacts/${id}/portal-chats`),
  getPortalWarranties: (id) => api.get(`/contacts/${id}/portal-warranties`),
  getTasks: (id) => api.get(`/contacts/${id}/tasks`),
  createTask: (id, data) => api.post(`/contacts/${id}/tasks`, data),
  getDeals: (id) => api.get(`/contacts/${id}/deals`),
  // Contact Products endpoints
  getProducts: (id) => api.get(`/contacts/${id}/products`),
  addProduct: (id, data) => api.post(`/contacts/${id}/products`, data),
  addBulkProducts: (id, products) => api.post(`/contacts/${id}/products/bulk`, products),
  updateProduct: (contactId, productRecordId, data) => api.put(`/contacts/${contactId}/products/${productRecordId}`, data),
  deleteProduct: (contactId, productRecordId) => api.delete(`/contacts/${contactId}/products/${productRecordId}`),
  // Contact Campaign History endpoints
  getCampaigns: (id) => api.get(`/contacts/${id}/campaigns`),
  addCampaign: (id, data) => api.post(`/contacts/${id}/campaigns`, data),
  updateCampaign: (contactId, campaignRecordId, data) => api.put(`/contacts/${contactId}/campaigns/${campaignRecordId}`, data),
  // Engagement Score
  getEngagement: (id) => api.get(`/campaign-automation/contacts/${id}/engagement`),
  evaluateRules: (id) => api.post(`/campaign-automation/contacts/${id}/evaluate-rules`),
  getTriggeredAutomations: (id) => api.get(`/campaign-automation/contacts/${id}/triggered-automations`),
  addAutomationTag: (id, data) => api.post(`/campaign-automation/contacts/${id}/tags`, data),
  removeAutomationTag: (id, tagName) => api.delete(`/campaign-automation/contacts/${id}/tags/${tagName}`),
};

// Campaign Automation Rules API
export const automationRulesAPI = {
  getAll: (activeOnly = false) => api.get('/campaign-automation/rules', { params: { active_only: activeOnly } }),
  get: (id) => api.get(`/campaign-automation/rules/${id}`),
  create: (data) => api.post('/campaign-automation/rules', data),
  update: (id, data) => api.put(`/campaign-automation/rules/${id}`, data),
  delete: (id) => api.delete(`/campaign-automation/rules/${id}`),
  toggle: (id) => api.post(`/campaign-automation/rules/${id}/toggle`),
  createPresets: () => api.post('/campaign-automation/rules/create-presets'),
};

// Customer Segments API
export const segmentsAPI = {
  getAll: (includeInactive = false) => api.get('/segments', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/segments', data),
  update: (id, data) => api.put(`/segments/${id}`, data),
  delete: (id) => api.delete(`/segments/${id}`),
};

// Product Lines API
export const productLinesAPI = {
  getAll: (includeInactive = false) => api.get('/product-lines', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/product-lines', data),
  update: (id, data) => api.put(`/product-lines/${id}`, data),
  delete: (id) => api.delete(`/product-lines/${id}`),
};

// Company Branding API
export const brandingAPI = {
  get: () => api.get('/branding'),
  update: (data) => api.put('/branding', data),
  uploadLogo: (file, logoType = 'logo') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('logo_type', logoType);
    return api.post('/branding/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Team Hierarchy API
export const teamAPI = {
  getHierarchy: () => api.get('/team/hierarchy'),
  getMyTeam: () => api.get('/team/my-team'),
  getMyManager: () => api.get('/team/my-manager'),
  createHierarchy: (data) => api.post('/team/hierarchy', data),
  updateHierarchy: (id, data) => api.put(`/team/hierarchy/${id}`, null, { params: data }),
  removeHierarchy: (id) => api.delete(`/team/hierarchy/${id}`),
  getUsers: () => api.get('/users/team'),
};

// Meeting Types API
export const meetingTypesAPI = {
  getAll: () => api.get('/meeting-types'),
  create: (data) => api.post('/meeting-types', data),
  delete: (id) => api.delete(`/meeting-types/${id}`),
};

// Agent Availability API
export const agentAvailabilityAPI = {
  get: () => api.get('/agent/availability'),
  update: (data) => api.put('/agent/availability', data),
  getBlocks: () => api.get('/agent/unavailable-blocks'),
  createBlock: (data) => api.post('/agent/unavailable-blocks', data),
  deleteBlock: (id) => api.delete(`/agent/unavailable-blocks/${id}`),
};

// Agent Dashboard API
export const agentDashboardAPI = {
  get: () => api.get('/agent/dashboard'),
  getWeeklyStats: (weeksBack = 4) => api.get('/agent/weekly-stats', { params: { weeks_back: weeksBack } }),
  getBookings: (status) => api.get('/agent/bookings', { params: { status } }),
  updateBookingStatus: (bookingId, status) => api.put(`/agent/bookings/${bookingId}/status`, null, { params: { status } }),
};

// Manager Oversight API
export const managerAPI = {
  getAlerts: (isRead) => api.get('/manager/alerts', { params: { is_read: isRead } }),
  markAlertRead: (alertId) => api.put(`/manager/alerts/${alertId}/read`),
  getTeamStats: () => api.get('/manager/team-stats'),
  getDashboard: (period = 'month') => api.get('/manager/dashboard', { params: { period } }),
};

// Holiday Calendar API
export const holidaysAPI = {
  getAll: (year) => api.get('/holidays', { params: year ? { year } : {} }),
  create: (data) => api.post('/holidays', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`),
};

// Sales Targets API
export const salesTargetsAPI = {
  getAll: (params) => api.get('/sales-targets', { params }),
  getCurrent: () => api.get('/sales-targets/current'),
  create: (data) => api.post('/sales-targets', data),
  update: (id, data) => api.put(`/sales-targets/${id}`, data),
  delete: (id) => api.delete(`/sales-targets/${id}`),
};

// Live Chat API
export const liveChatAPI = {
  // Settings
  getSettings: () => api.get('/live-chat/settings'),
  updateSettings: (data) => api.put('/live-chat/settings', data),
  // Agent Availability
  getAvailability: () => api.get('/live-chat/availability'),
  updateAvailability: (data) => api.put('/live-chat/availability', data),
  getAvailableAgents: () => api.get('/live-chat/available-agents'),
  // Sessions
  createSession: (data) => api.post('/live-chat/sessions', data),
  getSessions: (status) => api.get('/live-chat/sessions', { params: status ? { status } : {} }),
  getSession: (id) => api.get(`/live-chat/sessions/${id}`),
  assignChat: (id) => api.put(`/live-chat/sessions/${id}/assign`),
  closeChat: (id) => api.put(`/live-chat/sessions/${id}/close`),
  rateChat: (id, rating, feedback) => api.post(`/live-chat/sessions/${id}/rate`, null, { params: { rating, feedback } }),
  // Messages
  getMessages: (sessionId) => api.get(`/live-chat/sessions/${sessionId}/messages`),
  sendMessage: (sessionId, data) => api.post(`/live-chat/sessions/${sessionId}/messages`, data),
  // Transcripts
  getTranscript: (sessionId, format = 'json') => api.get(`/live-chat/sessions/${sessionId}/transcript`, { params: { format } }),
  saveTranscript: (sessionId) => api.post(`/live-chat/sessions/${sessionId}/save-transcript`),
  getSavedTranscripts: (limit = 50, skip = 0) => api.get('/live-chat/transcripts', { params: { limit, skip } }),
  getSavedTranscript: (id) => api.get(`/live-chat/transcripts/${id}`),
  // Analytics
  getAnalytics: (period = 'week') => api.get('/live-chat/analytics', { params: { period } }),
};

// Announcements API
export const announcementsAPI = {
  getAll: () => api.get('/announcements'),
  create: (data) => api.post('/announcements', data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

// Notes API
export const notesAPI = {
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Meetings API
export const meetingsAPI = {
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
};

// Subscriptions API
export const subscriptionsAPI = {
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
};

// Invoices API
export const invoicesAPI = {
  update: (id, data) => api.put(`/invoices/${id}`, data),
};

// Playbooks API
export const playbooksAPI = {
  update: (id, data) => api.put(`/playbooks/${id}`, data),
};

// Attachments API
export const attachmentsAPI = {
  delete: (id) => api.delete(`/attachments/${id}`),
  upload: (contactId, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    return api.post(`/contacts/${contactId}/upload-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Companies API
export const companiesAPI = {
  getAll: (params) => api.get('/companies', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  getProfile: (id) => api.get(`/companies/${id}/profile`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  // Units
  getUnits: (companyId) => api.get(`/companies/${companyId}/units`),
  createUnit: (companyId, data) => api.post(`/companies/${companyId}/units`, data),
  updateUnit: (unitId, data) => api.put(`/company-units/${unitId}`, data),
  deleteUnit: (unitId) => api.delete(`/company-units/${unitId}`),
  // Products (Asset Tracking)
  getProducts: (companyId, params) => api.get(`/companies/${companyId}/products`, { params }),
  getProduct: (companyId, productId) => api.get(`/companies/${companyId}/products/${productId}`),
  createProduct: (companyId, data) => api.post(`/companies/${companyId}/products`, data),
  updateProduct: (companyId, productId, data) => api.put(`/companies/${companyId}/products/${productId}`, data),
  deleteProduct: (companyId, productId) => api.delete(`/companies/${companyId}/products/${productId}`),
  getProductStats: (companyId) => api.get(`/companies/${companyId}/products/stats`),
  // Tickets & Orders for Company
  getTickets: (companyId, params) => api.get('/tickets', { params: { company_id: companyId, ...params } }),
  getOrders: (companyId, params) => api.get('/orders', { params: { company_id: companyId, ...params } }),
};

// Deals API
export const dealsAPI = {
  getAll: (params) => api.get('/deals', { params }),
  getById: (id) => api.get(`/deals/${id}`),
  create: (data) => api.post('/deals', data),
  update: (id, data) => api.put(`/deals/${id}`, data),
  delete: (id) => api.delete(`/deals/${id}`),
  // Tasks
  getTasks: (dealId, params) => api.get(`/deals/${dealId}/tasks`, { params }),
  createTask: (dealId, data) => api.post(`/deals/${dealId}/tasks`, data),
};

// Leads API
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convert: (id) => api.post(`/leads/${id}/convert`),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getUpcoming: () => api.get('/tasks/upcoming'),
  getAIHistory: (id) => api.get(`/tasks/${id}/ai-history`),
  getAIStatus: (id) => api.get(`/tasks/${id}/ai-status`),
};

// Gamification API
export const gamificationAPI = {
  getMyStats: () => api.get('/gamification/stats'),
  getUserStats: (userId) => api.get(`/gamification/stats/${userId}`),
  getLeaderboard: (period = 'all_time', limit = 10) => api.get('/gamification/leaderboard', { params: { period, limit } }),
  getAllBadges: () => api.get('/gamification/badges'),
};

// Playbook Notes API
export const playbookNotesAPI = {
  getAll: (entityType, entityId, playbookId = null) => api.get('/playbook-notes', { 
    params: { entity_type: entityType, entity_id: entityId, playbook_id: playbookId }
  }),
  create: (data) => api.post('/playbook-notes', data),
  update: (id, data) => api.put(`/playbook-notes/${id}`, data),
  delete: (id) => api.delete(`/playbook-notes/${id}`),
  addAIInteraction: (noteId, data) => api.post(`/playbook-notes/${noteId}/ai-interaction`, data),
  generateAISuggestion: (data) => api.post('/playbook-notes/generate-ai-suggestion', data),
};

// Sales Playbook API
export const salesPlaybookAPI = {
  getAll: () => api.get('/sales/playbooks'),
  getById: (id) => api.get(`/sales/playbooks/${id}`),
  create: (data) => api.post('/sales/playbooks', data),
  update: (id, data) => api.put(`/sales/playbooks/${id}`, data),
  delete: (id) => api.delete(`/sales/playbooks/${id}`),
  generate: (data) => api.post('/sales/playbooks/generate', data),
};

// Meetings API (extended)
export const meetingsAPIExtended = {
  getAll: () => api.get('/meetings'),
  getUpcoming: () => api.get('/meetings/upcoming'),
  getById: (id) => api.get(`/meetings/${id}`),
};

// Activities API
export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getRecent: (limit = 20) => api.get('/activities/recent', { params: { limit } }),
};

// Products API
export const productsAPI = {
  getAll: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
};

// Resource Documents API
export const resourceDocumentsAPI = {
  getAll: (params) => api.get('/resource-documents', { params }),
  getById: (id) => api.get(`/resource-documents/${id}`),
  create: (data) => api.post('/resource-documents', data),
  update: (id, data) => api.put(`/resource-documents/${id}`, data),
  delete: (id) => api.delete(`/resource-documents/${id}`),
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/resource-documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Document Access Tracking
  trackAccess: (documentId, accessType = 'view') => 
    api.post('/portal/document-access', null, { params: { document_id: documentId, access_type: accessType } }),
  getAccessStats: (documentId) => api.get('/document-access/stats', { params: { document_id: documentId } }),
  getAllAccessStats: () => api.get('/document-access/stats'),
  getAccessAlerts: () => api.get('/document-access/alerts'),
};

// Warranties API
export const warrantiesAPI = {
  getAll: (params) => api.get('/warranties', { params }),
  getById: (id) => api.get(`/warranties/${id}`),
  register: (data) => api.post('/warranties', data),
  update: (id, data) => api.put(`/warranties/${id}`, data),
  setExtendedWarranty: (id, apply) => api.post(`/warranties/${id}/extended-warranty`, { apply }),
  getRegions: () => api.get('/warranty-regions'),
};

// Resource Documents API (videos, FAQs, manuals, tools)
export const resourcesAPI = {
  getAll: (params) => api.get('/resource-documents', { params }),
  create: (data) => api.post('/resource-documents', data),
  update: (id, data) => api.put(`/resource-documents/${id}`, data),
  remove: (id) => api.delete(`/resource-documents/${id}`),
};

// Live chat canned responses API
export const cannedResponsesAPI = {
  getAll: () => api.get('/live-chat/canned-responses'),
  create: (data) => api.post('/live-chat/canned-responses', data),
  update: (id, data) => api.put(`/live-chat/canned-responses/${id}`, data),
  remove: (id) => api.delete(`/live-chat/canned-responses/${id}`),
};

// Portal usage analytics API
export const analyticsAPI = {
  track: (data) => api.post('/portal/analytics/track', data),
  getSummary: (days = 30) => api.get('/portal/analytics/summary', { params: { days } }),
};

// Web push notifications API
export const pushAPI = {
  getPublicKey: () => api.get('/portal/push/public-key'),
  subscribe: (sub) => api.post('/portal/push/subscribe', sub),
  unsubscribe: (sub) => api.post('/portal/push/unsubscribe', sub),
  broadcast: (data) => api.post('/admin/push/broadcast', data),
};

// Tickets API
export const ticketsAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  escalate: (id, data) => api.post(`/tickets/${id}/escalate`, data),
  addAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Dashboard & Analytics
  getDashboardMetrics: () => api.get('/tickets/dashboard/metrics'),
  getUrgentTickets: () => api.get('/tickets/dashboard/urgent'),
  getSLAAlerts: () => api.get('/tickets/sla-alerts'),
  // SLA Config
  getSLAConfig: () => api.get('/sla-config'),
  updateSLAConfig: (data) => api.put('/sla-config', data),
};

// Campaigns API
export const campaignsAPI = {
  getAll: (params) => api.get('/marketing/campaigns', { params }),
  getById: (id) => api.get(`/marketing/campaigns/${id}`),
  create: (data) => api.post('/marketing/campaigns', data),
  update: (id, data) => api.put(`/marketing/campaigns/${id}`, data),
  delete: (id) => api.delete(`/marketing/campaigns/${id}`),
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  getById: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};

// Email Integration API
export const emailIntegrationAPI = {
  getSettings: () => api.get('/email-settings'),
  getSyncStatus: () => api.get('/email-settings/sync-status'),
  createSettings: (data) => api.post('/email-settings', data),
  updateSettings: (data) => api.put('/email-settings', data),
  deleteSettings: () => api.delete('/email-settings'),
  testConnection: () => api.post('/email-settings/test-connection'),
  triggerSync: () => api.post('/email-settings/sync'),
  sendEmail: (data) => api.post('/emails/send', data),
  getAllEmails: (params) => api.get('/emails/all', { params }),
};

// Business Signatures API
export const signaturesAPI = {
  getAll: () => api.get('/signatures'),
  getDefault: () => api.get('/signatures/default'),
  create: (data) => api.post('/signatures', data),
  update: (id, data) => api.put(`/signatures/${id}`, data),
  delete: (id) => api.delete(`/signatures/${id}`),
  preview: (id) => api.get(`/signatures/${id}/preview`),
};

// Email Sequences API
export const sequencesAPI = {
  getAll: (params) => api.get('/sequences', { params }),
  getById: (id) => api.get(`/sequences/${id}`),
  create: (data) => api.post('/sequences', data),
  update: (id, data) => api.put(`/sequences/${id}`, data),
  delete: (id) => api.delete(`/sequences/${id}`),
  getStats: (id) => api.get(`/sequences/${id}/stats`),
  getEnrollments: (id, params) => api.get(`/sequences/${id}/enrollments`, { params }),
  enroll: (id, data) => api.post(`/sequences/${id}/enroll`, data),
  cancelEnrollment: (enrollmentId) => api.post(`/enrollments/${enrollmentId}/cancel`),
};

// Scheduled Emails API
export const scheduledEmailsAPI = {
  getAll: (params) => api.get('/scheduled-emails', { params }),
  send: (id) => api.post(`/scheduled-emails/${id}/send`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Internal Memos API
export const memosAPI = {
  getAll: (params) => api.get('/memos', { params }),
  getById: (id) => api.get(`/memos/${id}`),
  create: (data) => api.post('/memos', data),
  update: (id, data) => api.put(`/memos/${id}`, data),
  delete: (id) => api.delete(`/memos/${id}`),
  addComment: (id, data) => api.post(`/memos/${id}/comments`, data),
  // Task management for memos
  getTasks: (id) => api.get(`/memos/${id}/tasks`),
  createTask: (id, data) => api.post(`/memos/${id}/tasks`, data),
};

// AI Workforce API
export const aiWorkforceAPI = {
  // Agents
  getAgents: () => api.get('/ai-workforce/agents'),
  getAgent: (id) => api.get(`/ai-workforce/agents/${id}`),
  createAgent: (data) => api.post('/ai-workforce/agents', data),
  updateAgent: (id, data) => api.put(`/ai-workforce/agents/${id}`, data),
  activateAgent: (id) => api.post(`/ai-workforce/agents/${id}/activate`),
  deactivateAgent: (id) => api.post(`/ai-workforce/agents/${id}/deactivate`),
  
  // Work Queue & Tasks
  getWorkQueue: () => api.get('/ai-workforce/queue'),
  getWorkItems: () => api.get('/ai-workforce/workspace'),
  executeTask: (taskId, data) => api.post(`/ai-workforce/tasks/${taskId}/execute`, data),
  getTaskDeliverables: (taskId) => api.get(`/ai-workforce/tasks/${taskId}/deliverables`),
  
  // Reasoning & Approvals
  getReasoning: (id) => api.get(`/ai-workforce/reasoning/${id}`),
  approveExecution: (executionId, data) => api.post(`/ai-workforce/executions/${executionId}/approve`, data),
  
  // Stats
  getStats: () => api.get('/ai-workforce/stats'),
  
  // Knowledge Base
  getKnowledge: (params) => api.get('/ai-workforce/knowledge', { params }),
  createKnowledge: (data) => api.post('/ai-workforce/knowledge', data),
  updateKnowledge: (id, data) => api.put(`/ai-workforce/knowledge/${id}`, data),
  deleteKnowledge: (id) => api.delete(`/ai-workforce/knowledge/${id}`),
  
  // Guidelines
  getGuidelines: (params) => api.get('/ai-workforce/guidelines', { params }),
  createGuideline: (data) => api.post('/ai-workforce/guidelines', data),
  updateGuideline: (id, data) => api.put(`/ai-workforce/guidelines/${id}`, data),
  deleteGuideline: (id) => api.delete(`/ai-workforce/guidelines/${id}`),
  
  // SOPs (Standard Operating Procedures)
  getSOPs: (params) => api.get('/ai-workforce/sops', { params }),
  createSOP: (data) => api.post('/ai-workforce/sops', data),
  updateSOP: (id, data) => api.put(`/ai-workforce/sops/${id}`, data),
  deleteSOP: (id) => api.delete(`/ai-workforce/sops/${id}`),
  approveSOP: (id) => api.put(`/ai-workforce/sops/${id}/approve`),
  
  // Execution Logs
  getExecutionLogs: () => api.get('/ai-workforce/execution-logs'),
  
  // Quick Runs
  getQuickRuns: () => api.get('/ai-workforce/quick-runs'),
  createQuickRun: (data) => api.post('/ai-workforce/quick-runs', data),
  deleteQuickRun: (id) => api.delete(`/ai-workforce/quick-runs/${id}`),
  executeQuickRun: (id) => api.post(`/ai-workforce/quick-runs/${id}/run`),
  
  // Deliverables Gallery
  getDeliverables: (params) => api.get('/ai-workforce/deliverables', { params }),
  getDeliverable: (id) => api.get(`/ai-workforce/deliverables/${id}`),
  updateDeliverable: (id, data) => api.put(`/ai-workforce/deliverables/${id}`, data),
  deleteDeliverable: (id) => api.delete(`/ai-workforce/deliverables/${id}`),
  getDeliverablesStats: () => api.get('/ai-workforce/deliverables/stats/summary'),
  
  // Deliverable Variations & Revisions
  regenerateDeliverable: (id, feedback, numVariations = 3) => 
    api.post(`/ai-workforce/deliverables/${id}/regenerate`, { feedback, num_variations: numVariations }),
  selectVariation: (id, variationIndex, feedback) => 
    api.post(`/ai-workforce/deliverables/${id}/select-variation`, { variation_index: variationIndex, feedback }),
  getDeliverableHistory: (id) => api.get(`/ai-workforce/deliverables/${id}/history`),
  revertDeliverable: (id, version) => api.post(`/ai-workforce/deliverables/${id}/revert`, null, { params: { version } }),
  
  // Deliverable Export & Import
  exportToExcel: (deliverableId) => api.post(`/ai-workforce/deliverables/${deliverableId}/export/excel`, {}, { responseType: 'blob' }),
  exportToWord: (deliverableId) => api.post(`/ai-workforce/deliverables/${deliverableId}/export/word`, {}, { responseType: 'blob' }),
  previewImport: (deliverableId) => api.get(`/ai-workforce/deliverables/${deliverableId}/preview-import`),
  importToProspects: (deliverableId, fieldMapping, campaignId) => 
    api.post('/ai-workforce/deliverables/import-to-prospects', { 
      deliverable_id: deliverableId, 
      field_mapping: fieldMapping,
      campaign_id: campaignId 
    }),
  
  // AI Projects
  getProjects: (params) => api.get('/ai-workforce/projects', { params }),
  getProject: (id) => api.get(`/ai-workforce/projects/${id}`),
  getProjectKanban: (id) => api.get(`/ai-workforce/projects/${id}/kanban`),
  linkProject: (id, data) => api.put(`/ai-workforce/projects/${id}/link`, data),
  linkProjectToCampaign: (projectId, campaignId, createSubproject, subprojectTitle) => 
    api.post(`/ai-workforce/projects/${projectId}/link-campaign`, { campaign_id: campaignId, create_subproject: createSubproject, subproject_title: subprojectTitle }),
  
  // Deliverable to Campaign
  linkDeliverableToCampaign: (deliverableId, campaignId) => 
    api.post(`/ai-workforce/deliverables/${deliverableId}/link-campaign`, { campaign_id: campaignId }),
  
  // Campaign AI Work
  getCampaignAIWork: (campaignId) => api.get(`/ai-workforce/campaigns/${campaignId}/ai-work`),
  
  // Task Detail (with AI reasoning)
  getTaskDetail: (taskId) => api.get(`/ai-workforce/tasks/${taskId}`),
  getTaskReasoning: (taskId) => api.get(`/ai-workforce/tasks/${taskId}/reasoning`),
  
  // All AI Tasks (Global View)
  getAllAITasks: (params) => api.get('/ai-workforce/all-ai-tasks', { params }),
  processAllPendingTasks: () => api.post('/ai-workforce/tasks/process-all-pending'),
  generateBulkDeliverables: (taskId, types) => api.post(`/ai-workforce/tasks/${taskId}/bulk-deliverables`, types),
  
  // Resource Library
  getResources: (params) => api.get('/ai-workforce/resources', { params }),
  getResource: (id) => api.get(`/ai-workforce/resources/${id}`),
  createResource: (data) => api.post('/ai-workforce/resources', data),
  updateResource: (id, data) => api.put(`/ai-workforce/resources/${id}`, data),
  deleteResource: (id) => api.delete(`/ai-workforce/resources/${id}`),
  searchResourcesForAI: (query, category) => api.get('/ai-workforce/resources/ai/search', { params: { query, category } }),
  
  // Asset Library Integration
  getLinkedAssets: (params) => api.get('/ai-workforce/resources/linked-assets', { params }),
  syncResourceToAssetLibrary: (resourceId, folderId) => api.post('/ai-workforce/resources/sync-to-asset-library', null, { params: { resource_id: resourceId, folder_id: folderId } }),
  importAssetToResources: (assetId, category) => api.post('/ai-workforce/resources/import-from-asset-library', null, { params: { asset_id: assetId, category } }),
  getCombinedResources: (params) => api.get('/ai-workforce/resources/combined', { params }),
  
  // Overnight Work Queue
  getOvernightQueue: () => api.get('/ai-workforce/overnight-queue'),
  addToOvernightQueue: (data) => api.post('/ai-workforce/overnight-queue', null, { params: data }),
  removeFromOvernightQueue: (taskId) => api.delete(`/ai-workforce/overnight-queue/${taskId}`),
  updateOvernightTask: (taskId, data) => api.put(`/ai-workforce/overnight-queue/${taskId}`, data),
  runOvernightQueueNow: () => api.post('/ai-workforce/overnight-queue/run-now'),
  clearCompletedOvernightTasks: () => api.post('/ai-workforce/overnight-queue/clear-completed'),
  getOvernightQueueConfig: () => api.get('/ai-workforce/overnight-queue/config'),
  updateOvernightQueueConfig: (data) => api.put('/ai-workforce/overnight-queue/config', null, { params: data }),
  getOvernightQueueLogs: (limit = 50) => api.get('/ai-workforce/overnight-queue/logs', { params: { limit } }),
  deleteOvernightLog: (logId) => api.delete(`/ai-workforce/overnight-queue/logs/${logId}`),
  clearAllOvernightLogs: () => api.delete('/ai-workforce/overnight-queue/logs'),
  
  // Custom Processes (Workflow Templates)
  getCustomProcesses: () => api.get('/ai-workforce/custom-processes'),
  getCustomProcess: (id) => api.get(`/ai-workforce/custom-processes/${id}`),
  createCustomProcess: (data) => api.post('/ai-workforce/custom-processes', data.tasks, { 
    params: { name: data.name, description: data.description, schedule_type: data.schedule_type }
  }),
  updateCustomProcess: (id, data) => api.put(`/ai-workforce/custom-processes/${id}`, data.tasks, { params: data }),
  deleteCustomProcess: (id) => api.delete(`/ai-workforce/custom-processes/${id}`),
  queueCustomProcess: (id) => api.post(`/ai-workforce/custom-processes/${id}/queue`),
  
  // Video Generation (Sora 2)
  generateVideo: (data) => api.post('/ai-workforce/generate-video', data),
};

// Team Users API (for assignment dropdowns)
export const teamUsersAPI = {
  getUsers: () => api.get('/users/team'),
};

// Legal Check API
export const legalCheckAPI = {
  check: (content, contentType = 'text') => api.post('/legal-check', { content, content_type: contentType }),
};

// Engagement Campaigns API
export const engagementAPI = {
  getAll: (params) => api.get('/engagement-campaigns', { params }),
  getById: (id) => api.get(`/engagement-campaigns/${id}`),
  create: (data) => api.post('/engagement-campaigns', data),
  update: (id, data) => api.put(`/engagement-campaigns/${id}`, data),
  delete: (id) => api.delete(`/engagement-campaigns/${id}`),
  activate: (id) => api.post(`/engagement-campaigns/${id}/activate`),
  pause: (id) => api.post(`/engagement-campaigns/${id}/pause`),
  getScheduled: (id, params) => api.get(`/engagement-campaigns/${id}/scheduled`, { params }),
  sendNow: (campaignId, engagementId) => api.post(`/engagement-campaigns/${campaignId}/send-now`, null, { params: { engagement_id: engagementId } }),
  trigger: (triggerType, contactId, entityId, entityType) => 
    api.post(`/engagement-campaigns/trigger/${triggerType}`, null, { params: { contact_id: contactId, entity_id: entityId, entity_type: entityType } }),
  submitResponse: (engagementId, data) => api.post(`/engagements/${engagementId}/respond`, data),
  getStats: () => api.get('/engagement-stats'),
};

// System Email Templates API
export const systemEmailTemplatesAPI = {
  getAll: (params) => api.get('/system-email-templates', { params }),
  getById: (id) => api.get(`/system-email-templates/${id}`),
  create: (data) => api.post('/system-email-templates', data),
  update: (id, data) => api.put(`/system-email-templates/${id}`, data),
  delete: (id) => api.delete(`/system-email-templates/${id}`),
  reset: (id) => api.post(`/system-email-templates/${id}/reset`),
  preview: (id, contactId) => api.post(`/system-email-templates/${id}/preview`, null, { params: { contact_id: contactId } }),
};

// AI Sales Assistant API
export const aiAssistantAPI = {
  getInsights: (contactId) => api.get(`/contacts/${contactId}/ai-insights`),
  getLatestInsight: (contactId) => api.get(`/contacts/${contactId}/ai-insights/latest`),
  getInsightsHistory: (contactId, limit = 10, includeArchived = false) => 
    api.get(`/contacts/${contactId}/ai-insights/history`, { params: { limit, include_archived: includeArchived } }),
  createTaskFromRecommendation: (contactId, data) => api.post(`/contacts/${contactId}/ai-insights/create-task`, null, { params: data }),
  getDashboard: () => api.get('/ai-insights/dashboard'),
  archiveInsight: (insightId) => api.put(`/ai-insights/${insightId}/archive`),
  unarchiveInsight: (insightId) => api.put(`/ai-insights/${insightId}/unarchive`),
  getPriorityContacts: (limit = 10) => api.get('/contacts/priority-list', { params: { limit } }),
};

// Item Comments & Follow-ups API (works for tickets, deals, tasks, notes, meetings, etc.)
export const itemAPI = {
  getComments: (itemType, itemId, includeInternal = true) => 
    api.get(`/${itemType}/${itemId}/comments`, { params: { include_internal: includeInternal } }),
  addComment: (itemType, itemId, data) => 
    api.post(`/${itemType}/${itemId}/comments`, data),
  getFollowups: (itemType, itemId) => 
    api.get(`/${itemType}/${itemId}/followups`),
  addFollowup: (itemType, itemId, data) => 
    api.post(`/${itemType}/${itemId}/followups`, data),
  completeFollowup: (itemType, itemId, followupId) => 
    api.put(`/${itemType}/${itemId}/followups/${followupId}/complete`),
  closeItem: (itemType, itemId) => 
    api.put(`/${itemType}/${itemId}/close`),
  reopenItem: (itemType, itemId) => 
    api.put(`/${itemType}/${itemId}/reopen`),
};

// Customer Portal API
export const portalAPI = {
  createAccess: (data) => api.post('/portal/create-access', data),
  checkAccess: (contactId) => api.get(`/portal/check-access/${contactId}`),
  adminResetPassword: (data) => api.post('/portal/admin-reset-password', data),
  login: (data) => api.post('/portal/login', data),
  requestReset: (email) => api.post('/portal/request-reset', { email }),
  resetPassword: (data) => api.post('/portal/reset-password', data),
  getProfile: () => api.get('/portal/me'),
  updateProfile: (data) => api.put('/portal/me', data),
  changePassword: (oldPassword, newPassword) => 
    api.put('/portal/change-password', null, { params: { old_password: oldPassword, new_password: newPassword } }),
  getTickets: () => api.get('/portal/tickets'),
  addTicketComment: (ticketId, data) => api.post(`/portal/tickets/${ticketId}/comments`, data),
  getWarranties: () => api.get('/portal/warranties'),
  submitWarrantyClaim: (data) => api.post('/portal/warranty-claims', data),
  registerWarranty: (data) => api.post('/portal/warranties/register', data),
  getNotificationPreferences: () => api.get('/portal/notification-preferences'),
  updateNotificationPreferences: (data) => api.put('/portal/notification-preferences', data),
  getSubscriptions: () => api.get('/portal/subscriptions'),
  getPayments: () => api.get('/portal/payments'),
  getNews: () => api.get('/portal/news'),
  getBookings: () => api.get('/portal/bookings'),
  cancelBooking: (bookingId) => api.put(`/portal/bookings/${bookingId}/cancel`),
  getResourceDocuments: (category) => api.get('/portal/resource-documents', { params: { category } }),
  getMyDocuments: (category) => api.get('/portal/my-documents', { params: { category } }),
  // Admin portal user management
  getAllUsers: () => api.get('/portal/users'),
  deleteUser: (userId) => api.delete(`/portal/users/${userId}`),
  updateUserStatus: (userId, isActive) => api.put(`/portal/users/${userId}/status`, null, { params: { is_active: isActive } }),
};

// Public Meeting Scheduler API (no auth required)
export const schedulerAPI = {
  getMeetingTypes: () => api.get('/public/meeting-types'),
  getAgents: (meetingTypeId) => api.get('/public/agents', { params: { meeting_type_id: meetingTypeId } }),
  getAvailableSlots: (agentId, meetingTypeId, date) => 
    api.get('/public/available-slots', { params: { agent_id: agentId, meeting_type_id: meetingTypeId, date } }),
  bookMeeting: (data) => api.post('/public/book-meeting', null, { params: data }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Brand Voice & AI Content API
export const brandVoiceAPI = {
  getSettings: () => api.get('/marketing/brand-voice'),
  updateSettings: (data) => api.put('/marketing/brand-voice', data),
};

export const aiContentAPI = {
  generate: (data) => api.post('/marketing/ai/generate-content', data),
};

// A/B Testing API
export const abTestingAPI = {
  getAll: (params) => api.get('/marketing/ab-tests', { params }),
  getById: (id) => api.get(`/marketing/ab-tests/${id}`),
  create: (data) => api.post('/marketing/ab-tests', data),
  update: (id, data) => api.put(`/marketing/ab-tests/${id}`, data),
  delete: (id) => api.delete(`/marketing/ab-tests/${id}`),
  updateMetrics: (id, data) => api.put(`/marketing/ab-tests/${id}/metrics`, data),
  conclude: (id, winningVariationId) => api.post(`/marketing/ab-tests/${id}/conclude`, null, { 
    params: winningVariationId ? { winning_variation_id: winningVariationId } : {} 
  }),
  getOverview: () => api.get('/marketing/ab-tests/summary/overview'),
  optimize: (id) => api.post(`/marketing/ab-tests/${id}/optimize`),
  autoOptimizeAll: () => api.post('/marketing/ab-tests/auto-optimize-all'),
  getSmartSchedule: (params) => api.post('/marketing/ab-tests/smart-schedule', null, { params }),
};

// Ideation API
export const ideationAPI = {
  getAll: (params) => api.get('/marketing/ideation', { params }),
  getById: (id) => api.get(`/marketing/ideation/${id}`),
  create: (data) => api.post('/marketing/ideation', data),
  update: (id, data) => api.put(`/marketing/ideation/${id}`, data),
  vote: (id, voteType) => api.post(`/marketing/ideation/${id}/vote?vote_type=${voteType}`),
  convert: (id) => api.post(`/marketing/ideation/${id}/convert`),
  // Notes
  addNote: (id, content) => api.post(`/marketing/ideation/${id}/notes?content=${encodeURIComponent(content)}`),
  updateNote: (id, noteId, content) => api.put(`/marketing/ideation/${id}/notes/${noteId}?content=${encodeURIComponent(content)}`),
  deleteNote: (id, noteId) => api.delete(`/marketing/ideation/${id}/notes/${noteId}`),
};

// Asset Repository API
export const assetAPI = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  analyze: (id) => api.post(`/assets/${id}/analyze`),
  trackUsage: (id, data) => api.post(`/assets/${id}/use`, null, { params: data }),
  // Folders
  getFolders: (parentId) => api.get('/assets/folders', { params: { parent_id: parentId } }),
  createFolder: (data) => api.post('/assets/folders', null, { params: data }),
  getFolderPath: (folderId) => api.get(`/assets/folders/${folderId}/path`),
  // Chunked Upload
  initUpload: (filename, fileSize, fileType, totalChunks) => 
    api.post('/assets/upload/init', null, { 
      params: { filename, file_size: fileSize, file_type: fileType, total_chunks: totalChunks } 
    }),
  uploadChunk: (uploadId, chunkIndex, chunk) => {
    const formData = new FormData();
    formData.append('chunk_index', chunkIndex);
    formData.append('chunk', chunk);
    return api.post(`/assets/upload/chunk/${uploadId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  completeUpload: (uploadId, data) => api.post(`/assets/upload/complete/${uploadId}`, null, { params: data }),
  cancelUpload: (uploadId) => api.delete(`/assets/upload/${uploadId}`),
  simpleUpload: (formData) => api.post('/assets/upload/simple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // ZIP Export
  exportZip: (params) => api.get('/assets/export/zip', { 
    params, 
    responseType: 'blob' 
  }),
};

// Campaign Templates API
export const templateAPI = {
  getAll: (params) => api.get('/campaign-templates', { params }),
  getById: (id) => api.get(`/campaign-templates/${id}`),
  create: (data) => api.post('/campaign-templates', data),
  createFromCampaign: (campaignId, name, description) => 
    api.post(`/campaign-templates/from-campaign/${campaignId}`, null, { params: { name, description } }),
  update: (id, data) => api.put(`/campaign-templates/${id}`, data),
  delete: (id) => api.delete(`/campaign-templates/${id}`),
  use: (id, campaignName) => api.post(`/campaign-templates/${id}/use`, null, { params: { campaign_name: campaignName } }),
};

// Design System API
export const designAPI = {
  get: () => api.get('/design-system'),
  update: (data) => api.put('/design-system', data),
  preview: (data) => api.post('/design-system/preview', data),
};

// Marketing Content API
export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  getById: (id) => api.get(`/content/${id}`),
  getByType: (contentType, params) => api.get(`/content/type/${contentType}`, { params }),
  getStats: () => api.get('/content/stats'),
  create: (data) => api.post('/content', data),
  update: (id, data) => api.put(`/content/${id}`, data),
  delete: (id) => api.delete(`/content/${id}`),
  addToCampaign: (contentId, campaignId) => api.post(`/content/${contentId}/add-to-campaign/${campaignId}`),
  schedule: (id, publishDate, title) => api.post(`/content/${id}/schedule`, null, { 
    params: { publish_date: publishDate, calendar_title: title } 
  }),
  analyzeWithAI: (id) => api.post(`/content/${id}/ai-analyze`),
  generateWithAI: (params) => api.post('/content/ai-generate', null, { params }),
};

// Landing Pages API
export const landingPageAPI = {
  getAll: (params) => api.get('/landing-pages', { params }),
  getById: (id) => api.get(`/landing-pages/${id}`),
  create: (data) => api.post('/landing-pages', data),
  update: (id, data) => api.put(`/landing-pages/${id}`, data),
  delete: (id) => api.delete(`/landing-pages/${id}`),
  publish: (id) => api.post(`/landing-pages/${id}/publish`),
  unpublish: (id) => api.post(`/landing-pages/${id}/unpublish`),
  getSubmissions: (id, params) => api.get(`/landing-pages/${id}/submissions`, { params }),
  submitForm: (slug, data) => api.post(`/landing-pages/submit/${slug}`, data),
  // Analytics & Click Tracking
  getAnalytics: (id, days = 30) => api.get(`/landing-pages/${id}/analytics`, { params: { days } }),
  getOverviewAnalytics: (days = 30) => api.get('/landing-pages/analytics/overview', { params: { days } }),
  trackClick: (pageId, data) => api.post(`/landing-pages/${pageId}/track-click`, data),
  trackScroll: (pageId, data) => api.post(`/landing-pages/${pageId}/track-scroll`, data),
  // A/B Tests
  getABTests: (pageId) => api.get(`/landing-pages/${pageId}/ab-tests`),
  createABTest: (pageId, data) => api.post(`/landing-pages/${pageId}/ab-tests`, data),
  updateABTest: (testId, data) => api.put(`/landing-pages/ab-tests/${testId}`, data),
  recordABView: (testId, variantId) => api.post(`/landing-pages/ab-tests/${testId}/record-view`, null, { params: { variant_id: variantId } }),
  recordABConversion: (testId, variantId) => api.post(`/landing-pages/ab-tests/${testId}/record-conversion`, null, { params: { variant_id: variantId } }),
};

// Social Media API
export const socialMediaAPI = {
  // OAuth & Connection
  getStatus: () => api.get('/social-media/oauth/status'),
  getAuthUrl: (platform) => api.get(`/social-media/oauth/authorize/${platform}`),
  disconnect: (platform) => api.delete(`/social-media/oauth/disconnect/${platform}`),
  // Publishing
  publish: (data) => api.post('/social-media/posts/publish', data),
  getPosts: (params) => api.get('/social-media/posts', { params }),
  getPost: (postId) => api.get(`/social-media/posts/${postId}`),
  // Landing Page Alerts
  getAlerts: () => api.get('/social-media/alerts/landing-pages'),
  createAlert: (data) => api.post('/social-media/alerts/landing-pages', data),
  updateAlert: (alertId, data) => api.put(`/social-media/alerts/landing-pages/${alertId}`, data),
  deleteAlert: (alertId) => api.delete(`/social-media/alerts/landing-pages/${alertId}`),
  checkAlerts: () => api.post('/social-media/alerts/landing-pages/check'),
};

// Marketing Automation Workflows API
export const workflowAPI = {
  getAll: (params) => api.get('/marketing/workflows', { params }),
  getById: (id) => api.get(`/marketing/workflows/${id}`),
  create: (data) => api.post('/marketing/workflows', data),
  update: (id, data) => api.put(`/marketing/workflows/${id}`, data),
  delete: (id) => api.delete(`/marketing/workflows/${id}`),
  // Execution
  execute: (data) => api.post('/automation/execute', data),
  trigger: (data) => api.post('/automation/trigger', data),
  getExecutions: (params) => api.get('/automation/executions', { params }),
  getExecution: (id) => api.get(`/automation/executions/${id}`),
  resumeExecution: (id) => api.post(`/automation/executions/${id}/resume`),
  cancelExecution: (id) => api.post(`/automation/executions/${id}/cancel`),
  processWaiting: () => api.post('/automation/process-waiting'),
  getStats: () => api.get('/automation/stats'),
};

// AI Tools API
export const aiToolsAPI = {
  generateCopy: (data) => api.post('/ai-tools/generate-copy', data),
  refineCopy: (data) => api.post('/ai-tools/refine-copy', data),
  analyzeSEO: (data) => api.post('/ai-tools/analyze-seo', data),
  scoreBrandVoice: (data) => api.post('/ai-tools/score-brand-voice', data),
  generateHeadlines: (topic, count = 5, style = 'engaging') => 
    api.post('/ai-tools/generate-headlines', null, { params: { topic, count, style } }),
  getHistory: (params) => api.get('/ai-tools/history', { params }),
};

// Forecasting & Budget API
export const forecastingAPI = {
  // Budgets
  getBudgets: (params) => api.get('/forecasting/budgets', { params }),
  createBudget: (data) => api.post('/forecasting/budgets', data),
  getBudget: (id) => api.get(`/forecasting/budgets/${id}`),
  updateBudget: (id, data) => api.put(`/forecasting/budgets/${id}`, data),
  recordBudgetActual: (budgetId, lineItemId, amount, notes) => 
    api.post(`/forecasting/budgets/${budgetId}/record-actual`, null, { 
      params: { line_item_id: lineItemId, actual_amount: amount, notes } 
    }),
  getConsolidatedBudget: (year) => api.get('/forecasting/budgets/consolidated', { params: { fiscal_year: year } }),
  getBudgetTemplates: (type) => api.get('/forecasting/budget-templates', { params: { budget_type: type } }),
  // Forecasts
  getForecasts: (params) => api.get('/forecasting/forecasts', { params }),
  createForecast: (data) => api.post('/forecasting/forecasts', data),
  getForecast: (id) => api.get(`/forecasting/forecasts/${id}`),
  updateForecast: (id, data) => api.put(`/forecasting/forecasts/${id}`, data),
  recordForecastActual: (id, periodIndex, actuals) => 
    api.post(`/forecasting/forecasts/${id}/record-actual`, actuals, { params: { period_index: periodIndex } }),
  // Reports
  getReports: (params) => api.get('/forecasting/reports', { params }),
  generateReport: (data) => api.post('/forecasting/reports/generate', data),
  getReport: (id) => api.get(`/forecasting/reports/${id}`),
  // Performance
  getPerformanceDashboard: () => api.get('/forecasting/performance/dashboard'),
  recordPerformanceSnapshot: (data) => api.post('/forecasting/performance/snapshot', data),
};

// AI Report Builder API
export const reportBuilderAPI = {
  generateReport: (data) => api.post('/reports/ai-generate', data),
  getGeneratedReports: (params) => api.get('/reports/generated', { params }),
  getReport: (id) => api.get(`/reports/generated/${id}`),
  deleteReport: (id) => api.delete(`/reports/generated/${id}`),
};

// Scheduled Reports API
export const scheduledReportsAPI = {
  getAll: (params) => api.get('/scheduled-reports', { params }),
  getById: (id) => api.get(`/scheduled-reports/${id}`),
  create: (data) => api.post('/scheduled-reports', data),
  update: (id, data) => api.put(`/scheduled-reports/${id}`, data),
  delete: (id) => api.delete(`/scheduled-reports/${id}`),
  toggle: (id) => api.post(`/scheduled-reports/${id}/toggle`),
  runNow: (id) => api.post(`/scheduled-reports/${id}/run-now`),
  getHistory: (id, params) => api.get(`/scheduled-reports/${id}/history`, { params }),
  getAssignableUsers: () => api.get('/scheduled-reports/users/assignable'),
};

// SEO Tools API
export const seoToolsAPI = {
  // Keyword Research
  researchKeywords: (data) => api.post('/seo/keywords/research', data),
  // On-Page Analysis
  analyzeOnPage: (data) => api.post('/seo/analyze/on-page', data),
  // Rank Tracking
  addRankEntry: (data) => api.post('/seo/rank-tracking/entry', data),
  getRankTracking: (params) => api.get('/seo/rank-tracking', { params }),
  deleteRankEntry: (id) => api.delete(`/seo/rank-tracking/${id}`),
  // Competitors
  addCompetitor: (data) => api.post('/seo/competitors', data),
  getCompetitors: () => api.get('/seo/competitors'),
  deleteCompetitor: (id) => api.delete(`/seo/competitors/${id}`),
  // Content Optimization
  optimizeContent: (data) => api.post('/seo/optimize/content', data),
  // Dashboard
  getDashboard: () => api.get('/seo/dashboard'),
  // Google Search Console Import
  importGSCCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/campaign-analytics/gsc/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Omni-Channel Campaigns API
export const omniChannelAPI = {
  // Dashboard
  getDashboard: () => api.get('/omni-channel/dashboard'),
  
  // Omni-Channel Campaigns
  getCampaigns: (params) => api.get('/omni-channel/campaigns', { params }),
  getCampaign: (id) => api.get(`/omni-channel/campaigns/${id}`),
  createCampaign: (data) => api.post('/omni-channel/campaigns', data),
  updateCampaign: (id, data) => api.put(`/omni-channel/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/omni-channel/campaigns/${id}`),
  
  // Channel Management
  addChannel: (campaignId, data) => api.post(`/omni-channel/campaigns/${campaignId}/channels`, data),
  updateChannel: (campaignId, channelType, data) => api.put(`/omni-channel/campaigns/${campaignId}/channels/${channelType}`, data),
  
  // Tasks
  createTask: (campaignId, data) => api.post(`/omni-channel/campaigns/${campaignId}/tasks`, data),
  updateTask: (campaignId, taskId, data) => api.put(`/omni-channel/campaigns/${campaignId}/tasks/${taskId}`, data),
  deleteTask: (campaignId, taskId) => api.delete(`/omni-channel/campaigns/${campaignId}/tasks/${taskId}`),
  
  // Comments
  addComment: (campaignId, content, channel = null) => 
    api.post(`/omni-channel/campaigns/${campaignId}/comments`, null, { params: { content, channel } }),
  
  // Calendar
  getCalendar: (startDate, endDate) => api.get('/omni-channel/calendar', { params: { start_date: startDate, end_date: endDate } }),
  addCalendarEvent: (campaignId, data) => api.post(`/omni-channel/campaigns/${campaignId}/calendar`, data),
  
  // Email Templates
  getEmailTemplates: (category = null) => api.get('/omni-channel/email/templates', { params: { category } }),
  createEmailTemplate: (data) => api.post('/omni-channel/email/templates', data),
  updateEmailTemplate: (id, data) => api.put(`/omni-channel/email/templates/${id}`, data),
  deleteEmailTemplate: (id) => api.delete(`/omni-channel/email/templates/${id}`),
  
  // Email Campaigns
  getEmailCampaigns: (params) => api.get('/omni-channel/email/campaigns', { params }),
  getEmailCampaign: (id) => api.get(`/omni-channel/email/campaigns/${id}`),
  createEmailCampaign: (data) => api.post('/omni-channel/email/campaigns', data),
  updateEmailCampaign: (id, data) => api.put(`/omni-channel/email/campaigns/${id}`, data),
  deleteEmailCampaign: (id) => api.delete(`/omni-channel/email/campaigns/${id}`),
  sendEmailCampaign: (id) => api.post(`/omni-channel/email/campaigns/${id}/send`),
  sendTestEmail: (id, testEmail) => api.post(`/omni-channel/email/campaigns/${id}/test`, null, { params: { test_email: testEmail } }),
  
  // Contact Lists
  getContactLists: () => api.get('/omni-channel/email/lists'),
  createContactList: (data) => api.post('/omni-channel/email/lists', data),
  createListFromSegment: (name, segmentId) => api.post('/omni-channel/email/lists/from-segment', null, { params: { name, segment_id: segmentId } }),
  
  // AI Features
  generateEmail: (topic, tone = 'professional', audience = 'general', callToAction = null) => 
    api.post('/omni-channel/ai/generate-email', null, { params: { topic, tone, audience, call_to_action: callToAction } }),
  optimizeCampaign: (campaignId) => api.post('/omni-channel/ai/optimize-campaign', null, { params: { campaign_id: campaignId } }),
  
  // Campaign Comparison
  compareCampaigns: (campaignIds) => api.post('/omni-channel/campaigns/compare', campaignIds),
};

// Campaign Analytics API
export const campaignAnalyticsAPI = {
  // Archive
  archiveCampaign: (id) => api.post(`/campaign-analytics/archive/${id}`),
  unarchiveCampaign: (id) => api.post(`/campaign-analytics/unarchive/${id}`),
  getArchived: (params) => api.get('/campaign-analytics/archived', { params }),
  getArchivedDetail: (id) => api.get(`/campaign-analytics/archived/${id}`),
  // Analysis
  analyzeCampaigns: (data) => api.post('/campaign-analytics/analyze', data),
  // Results Tracking
  addResults: (campaignId, data) => api.post(`/campaign-analytics/results/${campaignId}`, data),
  getResults: (campaignId, days = 30) => api.get(`/campaign-analytics/results/${campaignId}`, { params: { days } }),
  deleteResult: (resultId) => api.delete(`/campaign-analytics/results/${resultId}`),
  // Workflow History
  getWorkflowHistory: (campaignId, limit = 100) => api.get(`/campaign-analytics/workflow-history/${campaignId}`, { params: { limit } }),
  // GSC Integration
  importGSCCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/campaign-analytics/gsc/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getGSCOAuthUrl: () => api.get('/campaign-analytics/gsc/oauth-url'),
};

// Marketing Tasks API - Unified task management across Marketing Hub
export const marketingTasksAPI = {
  // Core CRUD
  getAll: (params) => api.get('/marketing/tasks', { params }),
  getById: (id) => api.get(`/marketing/tasks/${id}`),
  create: (data) => api.post('/marketing/tasks', data),
  update: (id, data) => api.put(`/marketing/tasks/${id}`, data),
  delete: (id) => api.delete(`/marketing/tasks/${id}`),
  
  // User-specific
  getMyTasks: (includeCompleted = false, limit = 50) => 
    api.get('/marketing/tasks/my-tasks', { params: { include_completed: includeCompleted, limit } }),
  getTeamTasks: (includeCompleted = false, limit = 100) => 
    api.get('/marketing/tasks/team-tasks', { params: { include_completed: includeCompleted, limit } }),
  getDashboard: () => api.get('/marketing/tasks/dashboard'),
  
  // Entity-specific
  getCampaignTasks: (campaignId, includeCompleted = true) => 
    api.get(`/marketing/tasks/campaign/${campaignId}`, { params: { include_completed: includeCompleted } }),
  getContentTasks: (contentId, includeCompleted = true) => 
    api.get(`/marketing/tasks/content/${contentId}`, { params: { include_completed: includeCompleted } }),
  
  // Bulk operations
  bulkAssign: (taskIds, assignedTo) => api.post('/marketing/tasks/bulk-assign', null, { params: { task_ids: taskIds, assigned_to: assignedTo } }),
  bulkUpdateStatus: (taskIds, status) => api.post('/marketing/tasks/bulk-update-status', null, { params: { task_ids: taskIds, status } }),
  
  // Checklist
  addChecklistItem: (taskId, item) => api.post(`/marketing/tasks/${taskId}/checklist`, null, { params: { item } }),
  updateChecklistItem: (taskId, itemId, completed) => api.put(`/marketing/tasks/${taskId}/checklist/${itemId}`, null, { params: { completed } }),
  deleteChecklistItem: (taskId, itemId) => api.delete(`/marketing/tasks/${taskId}/checklist/${itemId}`),
};

// Sales Quotes API - Quote management integrated with Products, Contacts, Deals
export const salesQuotesAPI = {
  // Core CRUD
  getAll: (params) => api.get('/sales-quotes', { params }),
  getById: (id) => api.get(`/sales-quotes/${id}`),
  create: (data) => api.post('/sales-quotes', data),
  update: (id, data) => api.put(`/sales-quotes/${id}`, data),
  delete: (id) => api.delete(`/sales-quotes/${id}`),
  
  // Dashboard
  getDashboard: () => api.get('/sales-quotes/dashboard'),
  
  // Line items
  addLineItem: (quoteId, item) => api.post(`/sales-quotes/${quoteId}/items`, item),
  removeLineItem: (quoteId, itemId) => api.delete(`/sales-quotes/${quoteId}/items/${itemId}`),
  
  // Status management
  send: (id) => api.post(`/sales-quotes/${id}/send`),
  accept: (id) => api.post(`/sales-quotes/${id}/accept`),
  reject: (id, reason) => api.post(`/sales-quotes/${id}/reject`, null, { params: { reason } }),
  convertToOrder: (id) => api.post(`/sales-quotes/${id}/convert-to-order`),
  updateStatus: (id, data) => api.put(`/sales-quotes/${id}/status`, data),
  
  // History
  getHistory: (id) => api.get(`/sales-quotes/${id}/history`),
  addHistoryNote: (id, note) => api.post(`/sales-quotes/${id}/history`, null, { params: { note } }),
  
  // PDF data
  getPdfData: (id) => api.get(`/sales-quotes/${id}/pdf-data`),
  
  // Entity-specific
  getByContact: (contactId, includeExpired = false) => 
    api.get(`/sales-quotes/contact/${contactId}`, { params: { include_expired: includeExpired } }),
  getByDeal: (dealId) => api.get(`/sales-quotes/deal/${dealId}`),
  createFromDeal: (dealId) => api.post(`/sales-quotes/from-deal/${dealId}`),
};

// Sales Workflow API - Workflow rules for sales approvals
export const salesWorkflowAPI = {
  // Rules
  getRules: (params) => api.get('/sales/workflow/rules', { params }),
  getRule: (id) => api.get(`/sales/workflow/rules/${id}`),
  createRule: (data) => api.post('/sales/workflow/rules', data),
  updateRule: (id, data) => api.put(`/sales/workflow/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/sales/workflow/rules/${id}`),
  createPresets: () => api.post('/sales/workflow/rules/presets'),
  
  // Evaluation
  evaluateRules: (entityType, entityId, action = 'create') => 
    api.post('/sales/workflow/evaluate', null, { params: { entity_type: entityType, entity_id: entityId, action } }),
  
  // Approvals
  getApprovals: (params) => api.get('/sales/workflow/approvals', { params }),
  createApproval: (ruleId, entityType, entityId, reason) => 
    api.post('/sales/workflow/approvals', null, { params: { rule_id: ruleId, entity_type: entityType, entity_id: entityId, request_reason: reason } }),
  approve: (requestId) => api.post(`/sales/workflow/approvals/${requestId}/approve`),
  reject: (requestId, reason) => api.post(`/sales/workflow/approvals/${requestId}/reject`, null, { params: { reason } }),
  
  // Dashboard
  getDashboard: () => api.get('/sales/workflow/dashboard'),
};

// Prospects API (renamed from Leads)
export const prospectsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convert: (id, data) => api.post(`/leads/${id}/convert`, data),
};

// Prospecting & Cold Calling API
export const prospectingAPI = {
  // Dashboard
  getDashboard: () => api.get('/prospecting/dashboard'),
  
  // Cold Calls
  getColdCalls: (params) => api.get('/prospecting/cold-calls', { params }),
  getColdCall: (id) => api.get(`/prospecting/cold-calls/${id}`),
  createColdCall: (data) => api.post('/prospecting/cold-calls', data),
  updateColdCall: (id, data) => api.put(`/prospecting/cold-calls/${id}`, data),
  deleteColdCall: (id) => api.delete(`/prospecting/cold-calls/${id}`),
  logCall: (id, data) => api.post(`/prospecting/cold-calls/${id}/log-call`, data),
  convertToProspect: (id, data) => api.post(`/prospecting/cold-calls/${id}/convert`, data),
  getCallQueue: (limit = 20) => api.get(`/prospecting/cold-calls/queue?limit=${limit}`),
  bulkImport: (data) => api.post('/prospecting/cold-calls/bulk-import', data),
  bulkUpdateStatus: (ids, status) => api.post('/prospecting/cold-calls/bulk-update-status', { ids, status }),
  
  // Cold Call Tasks
  getColdCallTasks: (coldCallId, params) => api.get(`/prospecting/cold-calls/${coldCallId}/tasks`, { params }),
  createColdCallTask: (coldCallId, data) => api.post(`/prospecting/cold-calls/${coldCallId}/tasks`, null, { params: data }),
  getPendingTasks: (assignedTo) => api.get('/prospecting/tasks/pending', { params: { assigned_to: assignedTo } }),
  
  // Lead Scoring
  getScoringWeights: () => api.get('/prospecting/scoring/weights'),
  calculateScore: (leadId) => api.post(`/prospecting/scoring/calculate/${leadId}`),
  recordScoringEvent: (leadId, eventType, metadata) => 
    api.post(`/prospecting/scoring/event?lead_id=${leadId}&event_type=${eventType}`, metadata),
  getLeaderboard: (params) => api.get('/prospecting/scoring/leaderboard', { params }),
  
  // AI Research
  findLookalikes: (data) => api.post('/prospecting/ai/find-lookalikes', data),
  researchCompany: (companyName, website) => 
    api.post(`/prospecting/ai/research-company?company_name=${encodeURIComponent(companyName)}${website ? `&website=${encodeURIComponent(website)}` : ''}`),
  getGeographicAnalysis: (data) => api.post('/prospecting/ai/geographic-analysis', data),
};

// VoIP Integration API
export const voipAPI = {
  // Status and Config
  getStatus: () => api.get('/voip/status'),
  getConfig: () => api.get('/voip/config'),
  updateConfig: (data) => api.put('/voip/config', null, { params: data }),
  
  // Calls
  initiateCall: (data) => api.post('/voip/calls/initiate', data),
  endCall: (callId, data) => api.post(`/voip/calls/${callId}/end`, null, { params: data }),
  updateCall: (callId, data) => api.patch(`/voip/calls/${callId}`, data),
  getCalls: (params) => api.get('/voip/calls', { params }),
  getContactCalls: (contactId) => api.get(`/voip/calls/contact/${contactId}`),
  
  // SMS
  sendSMS: (data) => api.post('/voip/sms/send', data),
  getSMSLogs: (params) => api.get('/voip/sms', { params }),
  getContactSMS: (contactId) => api.get(`/voip/sms/contact/${contactId}`),
  
  // Dashboard
  getDashboard: () => api.get('/voip/dashboard'),
};

// Customer Portal API (File Uploads, Testimonials & Support Tickets)
export const customerPortalAPI = {
  // File Uploads
  uploadFile: (formData) => api.post('/customer-portal/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyFiles: () => api.get('/customer-portal/files'),
  getFile: (fileId) => api.get(`/customer-portal/files/${fileId}`),
  deleteFile: (fileId) => api.delete(`/customer-portal/files/${fileId}`),
  
  // Admin File Management
  adminGetAllFiles: (params) => api.get('/customer-portal/admin/files', { params }),
  adminGetFile: (fileId) => api.get(`/customer-portal/admin/files/${fileId}`),
  adminUpdateFile: (fileId, data) => api.put(`/customer-portal/admin/files/${fileId}`, data),
  
  // Admin Pending Counts (for in-app notifications)
  adminGetPendingCounts: () => api.get('/customer-portal/admin/pending-counts'),
  
  // Testimonials
  submitTestimonial: (data) => api.post('/customer-portal/testimonials', data),
  getMyTestimonials: () => api.get('/customer-portal/testimonials'),
  deleteTestimonial: (testimonialId) => api.delete(`/customer-portal/testimonials/${testimonialId}`),
  
  // Admin Testimonial Management
  adminGetAllTestimonials: (params) => api.get('/customer-portal/admin/testimonials', { params }),
  adminUpdateTestimonial: (testimonialId, data) => api.put(`/customer-portal/admin/testimonials/${testimonialId}`, data),
  
  // Public Testimonials (no auth required)
  getPublicTestimonials: (params) => api.get('/customer-portal/public/testimonials', { params }),
  
  // Support Tickets
  submitTicket: (formData) => api.post('/customer-portal/tickets', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyTickets: (params) => api.get('/customer-portal/tickets', { params }),
  getTicket: (ticketId) => api.get(`/customer-portal/tickets/${ticketId}`),
  addTicketComment: (ticketId, formData) => api.post(`/customer-portal/tickets/${ticketId}/comment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Admin Ticket Management
  adminGetAllTickets: (params) => api.get('/customer-portal/admin/tickets', { params }),
  adminUpdateTicket: (ticketId, params) => api.put(`/customer-portal/admin/tickets/${ticketId}`, null, { params }),
  adminReplyToTicket: (ticketId, formData) => api.post(`/customer-portal/admin/tickets/${ticketId}/reply`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Bug Reports API
export const bugReportsAPI = {
  getAll: (params) => api.get('/bug-reports', { params }),
  get: (bugId) => api.get(`/bug-reports/${bugId}`),
  create: (data) => api.post('/bug-reports', data),
  update: (bugId, data) => api.put(`/bug-reports/${bugId}`, data),
  delete: (bugId) => api.delete(`/bug-reports/${bugId}`),
  getStats: () => api.get('/bug-reports/stats/summary'),
  // Export for Emergent integration
  exportPrompt: (bugId) => api.get(`/bug-reports/${bugId}/export-prompt`),
  exportAllPrompts: (priority) => api.get('/bug-reports/export-all-prompts', { params: priority ? { priority } : {} }),
  // Archive functionality
  getVersions: () => api.get('/bug-reports/versions'),
  archive: (bugIds, version) => api.post('/bug-reports/archive', bugIds, { params: { version } }),
  unarchive: (bugIds) => api.post('/bug-reports/unarchive', bugIds),
  getCurrentVersion: () => api.get('/bug-reports/current-version'),
  updateCurrentVersion: (version) => api.put('/bug-reports/current-version', null, { params: { version } }),
};

// Affiliate API
export const affiliateAPI = {
  // Admin endpoints
  getAll: (params) => api.get('/affiliates', { params }),
  get: (id) => api.get(`/affiliates/${id}`),
  create: (data) => api.post('/affiliates', data),
  update: (id, data) => api.put(`/affiliates/${id}`, data),
  updateStatus: (id, status) => api.put(`/affiliates/${id}/status`, null, { params: { status } }),
  // Referrals
  getReferrals: (params) => api.get('/referrals', { params }),
  createReferral: (data) => api.post('/referrals', data),
  updateReferralStatus: (id, status, purchaseAmount) => 
    api.put(`/referrals/${id}/status`, null, { params: { status, purchase_amount: purchaseAmount } }),
  // Affiliate portal
  getDashboard: () => api.get('/affiliate/dashboard'),
  getResources: () => api.get('/affiliate/resources'),
};

// Smart Scheduling Assistant API
export const schedulingAPI = {
  // AI-powered suggestions
  getSuggestions: () => api.get('/scheduling/suggestions'),
  dismissSuggestion: (suggestionId) => api.post(`/scheduling/suggestions/${suggestionId}/dismiss`),
  
  // Task patterns and insights
  getPatterns: () => api.get('/scheduling/patterns'),
  
  // Availability checking
  checkAvailability: (date) => api.get('/scheduling/availability', { params: { date } }),
  getOptimalSlots: (params) => api.get('/scheduling/optimal-slots', { params }),
  
  // Microsoft Calendar integration
  getMicrosoftCalendarStatus: () => api.get('/scheduling/microsoft/status'),
  initMicrosoftAuth: () => api.get('/scheduling/microsoft/auth'),
  disconnectMicrosoftCalendar: () => api.post('/scheduling/microsoft/disconnect'),
  syncMicrosoftEvents: () => api.post('/scheduling/microsoft/sync'),
  getMicrosoftEvents: (params) => api.get('/scheduling/microsoft/events', { params }),
  createMicrosoftEvent: (data) => api.post('/scheduling/microsoft/events', data),
};

// Admin System API
export const adminSystemAPI = {
  previewCleanup: () => api.get('/admin/system/cleanup-preview'),
  executeCleanup: (data) => api.post('/admin/system/cleanup', data),
};

export default api;
